/**
 * MAATII cover library — single source of truth for picker + fallbacks.
 *
 * HOW TO ADD YOUR OWN CURATED FILES
 * ─────────────────────────────────
 * 1. Drop images under `public/covers/<type>/` (e.g. `public/covers/gathering/summer-porch.jpg`).
 * 2. Append objects to `CURATED_COVER_OPTIONS` below (not to legacy).
 *    - `src`: use site-relative paths like `/covers/gathering/summer-porch.jpg` (served from `public/`).
 *    - Optional `thumbSrc`: smaller WebP/JPEG for the horizontal strip; omit to reuse `src`.
 *    - Set `isCurated: true` (optional but recommended for clarity).
 * 3. Later you can delete rows from `LEGACY_COVER_OPTIONS` — no picker refactor needed.
 *
 * CLOUDFLARE IMAGES LATER
 * ───────────────────────
 * Replace `src` / `thumbSrc` with delivery URLs (or URL + params). Shapes stay the same.
 */

export const EVENT_COVER_TYPES = ["gathering", "celebration", "milestone"] as const

export type EventCoverType = (typeof EVENT_COVER_TYPES)[number]

export type CoverTone = "warm" | "bright" | "evening" | "indoor" | "outdoor"

/**
 * One selectable cover. Keep URLs/paths here only — not in components.
 *
 * Optional fields (recommended):
 * - `thumbSrc` — lighter URL for the picker strip; defaults to `src` if omitted.
 * - `alt` — screen reader + hero context; sensible default is derived from `id` if omitted.
 * - `isDefault` — exactly one `true` per `type` for auto-pick + submit fallback (legacy sets this today).
 */
export type CoverOption = {
  id: string
  src: string
  type: EventCoverType
  tags?: string[]
  tone?: CoverTone
  /** True when this row is from your `/public/covers/...` library (optional metadata). */
  isCurated?: boolean
  thumbSrc?: string
  alt?: string
  isDefault?: boolean
}

/** Max thumbnails in the horizontal strip (matches previous picker behavior). */
const PICKER_STRIP_MAX = 14

/* ─── Curated (local `/public/covers/...`) — add entries here as files land ─── */

export const CURATED_COVER_OPTIONS: CoverOption[] = [
  // Examples (uncomment and rename files when assets exist):
  // {
  //   id: "gathering-porch-01",
  //   src: "/covers/gathering/porch-golden-hour.jpg",
  //   thumbSrc: "/covers/gathering/porch-golden-hour-thumb.webp",
  //   type: "gathering",
  //   tags: ["porch", "friends", "golden-hour"],
  //   tone: "warm",
  //   alt: "Friends on a porch at golden hour",
  //   isCurated: true,
  //   isDefault: true,
  // },
  // {
  //   id: "celebration-table-01",
  //   src: "/covers/celebration/long-table-toast.jpg",
  //   type: "celebration",
  //   tags: ["toast", "table", "evening"],
  //   tone: "evening",
  //   isCurated: true,
  // },
]

/* ─── Legacy (current defaults / Unsplash) — remove when curated set is complete ─── */

/** Strip thumbs ~72–80px CSS; 2× DPR → ~160px is enough with next/image */
function unsplashThumb(photoId: string): string {
  return `https://images.unsplash.com/${photoId}?auto=format&fit=crop&w=160&h=160&q=68`
}

/** Narrow-column 2:1 hero; column max 40rem — ~840w covers ~1.3× for sharpness without 960w overfetch */
function unsplashHero(photoId: string): string {
  return `https://images.unsplash.com/${photoId}?auto=format&fit=crop&w=840&h=420&q=78`
}

function legacy(
  id: string,
  photoId: string,
  type: EventCoverType,
  tags: string[],
  tone: CoverTone,
  alt: string,
  isDefault?: boolean,
): CoverOption {
  return {
    id,
    src: unsplashHero(photoId),
    thumbSrc: unsplashThumb(photoId),
    type,
    tags,
    tone,
    alt,
    isCurated: false,
    ...(isDefault ? { isDefault: true } : {}),
  }
}

/**
 * Fallback placeholders until your curated files replace them.
 * Safe to delete in one pass once `CURATED_COVER_OPTIONS` covers each type.
 */
export const LEGACY_COVER_OPTIONS: CoverOption[] = [
  legacy(
    "gathering-friends-toast",
    "photo-1529333166437-7750a6dd5a70",
    "gathering",
    ["friends", "wine", "laughter", "table"],
    "warm",
    "Friends mid-toast, natural light on the table",
    true,
  ),
  legacy(
    "gathering-cafe-window",
    "photo-1517248135467-4c7edcad34c4",
    "gathering",
    ["cafe", "conversation", "window light"],
    "indoor",
    "Two people talking over coffee by the window",
  ),
  legacy(
    "gathering-restaurant-long-table",
    "photo-1414235077428-338989a2e8c0",
    "gathering",
    ["dinner", "restaurant", "shared meal"],
    "evening",
    "Long table dinner, soft restaurant glow",
  ),
  legacy(
    "gathering-brunch-pancakes",
    "photo-1567620905732-2d1ec7ab7445",
    "gathering",
    ["brunch", "pancakes", "morning"],
    "bright",
    "Stacked pancakes and syrup — slow weekend morning",
  ),
  legacy(
    "gathering-passing-dishes",
    "photo-1556910103-1c02745aae4d",
    "gathering",
    ["kitchen", "hands", "shared food"],
    "warm",
    "Hands passing bowls across a busy counter",
  ),
  legacy(
    "gathering-park-grass",
    "photo-1529156069898-49953e39b3ac",
    "gathering",
    ["park", "grass", "group", "lazy afternoon"],
    "outdoor",
    "Friends lounging on the grass, easy afternoon",
  ),
  legacy(
    "celebration-cake-candles",
    "photo-1464349095431-e9a21285b5f3",
    "celebration",
    ["cake", "candles", "close", "birthday"],
    "warm",
    "Birthday cake and candles, tight warm frame",
    true,
  ),
  legacy(
    "celebration-fireworks-sky",
    "photo-1519671482749-fd09be7ccebf",
    "celebration",
    ["fireworks", "night", "sky", "joy"],
    "evening",
    "Fireworks opening across the night sky",
  ),
  legacy(
    "celebration-fairy-lights",
    "photo-1511795409834-ef04bbd61622",
    "celebration",
    ["lights", "evening", "gathered", "glow"],
    "evening",
    "Fairy lights overhead, faces in warm bokeh",
  ),
  legacy(
    "celebration-outdoor-friends",
    "photo-1511988617509-a57c8a288659",
    "celebration",
    ["outdoor", "friends", "laughter", "daylight"],
    "bright",
    "Friends laughing outside, sun on their shoulders",
  ),
  legacy(
    "celebration-night-crowd",
    "photo-1492684223066-81342ee5ff30",
    "celebration",
    ["party", "night", "energy", "together"],
    "evening",
    "Night out energy — real crowd, real noise",
  ),
  legacy(
    "celebration-feast-spread",
    "photo-1555939594-58d7cb561ad1",
    "celebration",
    ["food", "grill", "color", "abundance"],
    "bright",
    "Grilled feast laid out — celebration through food",
  ),
  legacy(
    "milestone-champagne-toast",
    "photo-1511578314322-379afb476865",
    "milestone",
    ["toast", "glasses", "clink", "achievement"],
    "warm",
    "Champagne glasses lifted in a quiet toast",
    true,
  ),
  legacy(
    "milestone-graduation-caps",
    "photo-1523240795612-9a054b0db644",
    "milestone",
    ["graduation", "caps", "sky", "relief"],
    "bright",
    "Graduation caps in the air against open sky",
  ),
  legacy(
    "milestone-running-track",
    "photo-1571019613454-1cb2f99b2d8b",
    "milestone",
    ["training", "track", "effort", "focus"],
    "outdoor",
    "On the track — effort you can feel",
  ),
  legacy(
    "milestone-team-desk",
    "photo-1517245386807-bb43f82c33c4",
    "milestone",
    ["team", "desk", "win", "together"],
    "indoor",
    "Team leaning in around a screen, real office light",
  ),
  legacy(
    "milestone-first-dance",
    "photo-1508672019048-805c876b67e2",
    "milestone",
    ["dance", "couple", "moment", "soft light"],
    "evening",
    "First-dance stillness in warm light",
  ),
  legacy(
    "milestone-sunrise-path",
    "photo-1476480862126-209bfaa8edc8",
    "milestone",
    ["dawn", "path", "quiet", "next step"],
    "outdoor",
    "Sunrise on the trail — a personal finish line",
  ),
]

/** When an event has no persisted image — keep in sync with a warm legacy hero you are OK showing in grids. */
export const EVENT_COVER_FALLBACK_IMAGE_SRC =
  "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?auto=format&fit=crop&w=1200&h=600&q=80"

function optionsForType(type: EventCoverType): CoverOption[] {
  const curated = CURATED_COVER_OPTIONS.filter((o) => o.type === type)
  const legacy = LEGACY_COVER_OPTIONS.filter((o) => o.type === type)
  if (curated.length === 0) return legacy
  const curatedIds = new Set(curated.map((c) => c.id))
  const legacyDeduped = legacy.filter((l) => !curatedIds.has(l.id))
  return [...curated, ...legacyDeduped]
}

/**
 * Balanced strip: round-robin across types using merged lists (curated → legacy per type).
 */
function balancedCoverOptions(maxItems: number = PICKER_STRIP_MAX): CoverOption[] {
  const byType: Record<EventCoverType, CoverOption[]> = {
    gathering: optionsForType("gathering"),
    celebration: optionsForType("celebration"),
    milestone: optionsForType("milestone"),
  }
  const out: CoverOption[] = []
  const seen = new Set<string>()
  let round = 0
  let added = true
  while (out.length < maxItems && added) {
    added = false
    for (const t of EVENT_COVER_TYPES) {
      const item = byType[t][round]
      if (item && !seen.has(item.id)) {
        seen.add(item.id)
        out.push(item)
        added = true
        if (out.length >= maxItems) break
      }
    }
    round += 1
    if (round > 24) break
  }
  return out
}

/**
 * Picker feed: merged curated + legacy for a type, or a balanced mix when `eventType` is omitted/null.
 */
export function getCoverOptions(
  eventType?: EventCoverType | null,
  maxItems: number = PICKER_STRIP_MAX,
): CoverOption[] {
  if (eventType == null) return balancedCoverOptions(maxItems)
  return optionsForType(eventType).slice(0, maxItems)
}

export function getDefaultCoverOption(type: EventCoverType): CoverOption | null {
  const merged = optionsForType(type)
  const marked = merged.find((o) => o.isDefault)
  if (marked) return marked
  return merged[0] ?? null
}

export function findCoverOptionById(id: string): CoverOption | null {
  const all = [...CURATED_COVER_OPTIONS, ...LEGACY_COVER_OPTIONS]
  return all.find((o) => o.id === id) ?? null
}

export function isEventCoverType(id: string): id is EventCoverType {
  return (EVENT_COVER_TYPES as readonly string[]).includes(id)
}
